import cx from 'classnames';
import { map } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'components/Modal';
import { CustomerCancelReason, EventName } from 'enums';
import { TranslationNamespace } from 'i18n';
import { eventUtils } from 'utils';

import { SqCheckmark } from '../SqCheckmark';

export type SqCancelFeedback = {
  reasons: CustomerCancelReason[];
  comment: string;
};

type Props = {
  open: boolean;
  reasons?: CustomerCancelReason[];
  onConfirm: (feedback: SqCancelFeedback) => void;
  onDismiss: () => void;
};

const COMMENT_MAX_LENGTH = 300;

export const SqCancelModal: React.FC<Props> = ({
  open,
  reasons = [],
  onConfirm,
  onDismiss,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_cancel_modal',
  });
  const { t: tSurvey } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.cancel_survey_modal',
  });

  const [selectedReasons, setSelectedReasons] = useState<
    CustomerCancelReason[]
  >([]);
  const [comment, setComment] = useState('');

  const toggleReason = useCallback((reason: CustomerCancelReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  }, []);

  const handleConfirm = useCallback(() => {
    eventUtils.trackEvent({
      event: EventName.CancelSurveyConfirm,
      message: JSON.stringify({ reasons: selectedReasons, comment }),
    });
    onConfirm({ reasons: selectedReasons, comment });
    setSelectedReasons([]);
    setComment('');
  }, [onConfirm, selectedReasons, comment]);

  const handleDismiss = useCallback(() => {
    eventUtils.trackEvent({
      event: EventName.CancelSurveyDismiss,
    });
    onDismiss();
    setSelectedReasons([]);
    setComment('');
  }, [onDismiss]);

  return (
    <Modal
      open={open}
      mobileViewOnly
      onClose={handleDismiss}
      title={t('title')}
      footer={
        <>
          <button
            data-testid="sq-cancel-modal-dismiss-button"
            className="tw-w-full tw-h-[54px] tw-rounded-[24px] tw-bg-primary-button tw-text-primary-button tw-font-medium tw-text-base tw-border-none tw-cursor-pointer tw-mb-2"
            onClick={handleDismiss}
          >
            {t('continue_button')}
          </button>
          <button
            data-testid="sq-cancel-modal-confirm-button"
            className="tw-w-full tw-h-[54px] tw-rounded-[24px] tw-bg-transparent tw-text-cancel-action tw-font-medium tw-text-base tw-border-none tw-cursor-pointer"
            onClick={handleConfirm}
          >
            {t('cancel_button')}
          </button>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-2">
        <p className="tw-text-sm tw-text-secondary tw-mb-1">
          {t('description')}
        </p>

        <p className="tw-text-secondary tw-mt-2 tw-text-sm">
          {t('cancel_reason_hint')}
        </p>

        {reasons.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {map(reasons, (reason) => {
              const isSelected = selectedReasons.includes(reason);
              return (
                <div
                  key={reason}
                  className={cx(
                    'tw-relative tw-flex tw-items-center tw-px-4 tw-py-3.5 tw-rounded-2xl tw-cursor-pointer tw-transition-colors tw-border tw-border-solid',
                    isSelected
                      ? 'tw-bg-tertiary-button tw-border-component-accent'
                      : 'tw-bg-[var(--bg-color-option-unselected)] tw-border-transparent',
                  )}
                  onClick={() => toggleReason(reason)}
                >
                  <span className="tw-text-sm tw-font-medium tw-text-primary tw-pr-8">
                    {tSurvey(`reasons.${reason}`)}
                  </span>
                  {isSelected && (
                    <div className="tw-absolute tw-right-4">
                      <SqCheckmark />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="tw-bg-payment-layout-out tw-rounded-[24px] tw-px-4 tw-py-3">
          <textarea
            value={comment}
            onChange={(e) =>
              setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH))
            }
            placeholder={tSurvey('comment_placeholder')}
            maxLength={COMMENT_MAX_LENGTH}
            rows={4}
            className="tw-w-full tw-resize-none tw-bg-transparent tw-text-sm tw-font-medium tw-text-primary tw-outline-none tw-border-none tw-placeholder-secondary placeholder:tw-text-sm"
          />
          <div className="tw-text-right tw-text-xs tw-text-secondary">
            {`${comment.length}/${COMMENT_MAX_LENGTH}`}
          </div>
        </div>
      </div>
    </Modal>
  );
};
